import { DeleteOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons';
import {
  App,
  Button, Collapse, Flex, Select, Slider,
  Tooltip, Modal,
} from 'antd';
import SldStyleParser from 'geostyler-sld-parser';
import chroma from 'chroma-js';
import {
  useCallback,
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { ColorStep, Product } from 'types/app.types';
import { parseFromColorStepsToGeoStyle, parseFromGeoStyleToColorSteps } from 'utils';
import {
  useGetLayerStylesQuery, useUpdateLayerStyleMutation,
  useCreateLayerStyleMutation, useDeleteStyleAndUnlinkMutation,
} from 'services/geoserver';
import { useGetStylesQuery, useGetLegendQuery } from 'services/dtn';
import { DTNLegendList } from 'services/dtn/types';
import MapViewContext from 'contexts/MapViewContext';
import ProductsContext from 'contexts/ProductsContext';
import { getLayerByName, purgeCache, recreateSource } from 'contexts/MapViewContext/hooks/utils';
import MainAppContext from 'contexts/MainAppContext';
import TimeSeriesContext from 'contexts/TimeSeriesContext';
import { Group } from 'ol/layer';
import { Map } from 'ol';
import ImageLayer from 'ol/layer/Image';
import { ImageStatic } from 'ol/source';
import { createLogger } from 'services/debugLogger';
import ColorScale from '../ColorScale';
import ColorScaleEditorModal from './components/ColorScaleEditorModal';
import styles from './index.module.scss';
import styleTemplate from './style-template';
import LayerName from '../../../../../../../../../../../../../../contexts/MapViewContext/hooks/enums';

const logger = createLogger('ConfigPanel');

const { Option } = Select;
const { selector, header } = styles;

function legendToColorSteps(legend: DTNLegendList[number]): ColorStep[] {
  const all = legend.colorTable
    .filter((item) => item.value !== undefined && item.color !== '#00000000')
    .map((item) => ({
      color: chroma(item.color).rgba(),
      threshold: item.value!,
    }));

  if (all.length <= 10) return all;

  const step = (all.length - 1) / 9;
  return Array.from({ length: 10 }, (_, i) => all[Math.round(i * step)]);
}
interface ConfigPanelsProps {
    product: Product
}

/* interface ElevationInfo {
  elevations: number[],
  resolution: number
}

 const parseElevations = (elevations: string): ElevationInfo => {
  const splittedElevations = elevations.split('/').map((d) => parseInt(d, 10));

  if (splittedElevations.length) {
    return {
      elevations: [splittedElevations[0], splittedElevations[1]],
      resolution: splittedElevations[2],
    };
  }
  const elevationsList = elevations.split(',').map((d) => parseInt(d, 10));

  return {
    elevations: [elevationsList[0], elevationsList[elevationsList.length - 1]],
    resolution: elevationsList.reduce((prev, cur) => prev + cur) / elevationsList.length,
  };
}; */

const updateGeoLayer = (map: Map, currentProjection: string) => {
  try {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    const allLayers = geoLayer?.getLayers().getArray() as ImageLayer<ImageStatic>[];

    if (geoLayer) {
      allLayers.forEach((l) => {
        const source = recreateSource({
          layer: l,
          currentProjection,
        });
        l.setSource(source);
      });
    }
  } catch (e) {
    logger.error('Error updating layer source', e);
  }
};

function ConfigPanels({ product } : ConfigPanelsProps) {
  const isDTN = product.provider === 'dtn';
  const { data: allTheStyles } = useGetLayerStylesQuery({ layerName: product.internalName || '' }, { skip: !product.internalName || isDTN });
  const { data: dtnStyles } = useGetStylesQuery({ layerId: product.internalName || '' }, { skip: !product.internalName || !isDTN });
  const { data: dtnLegend } = useGetLegendQuery({ layerId: product.internalName || '' }, { skip: !product.internalName || !isDTN });
  const dtnColors = useMemo(
    () => (dtnLegend?.[0] ? legendToColorSteps(dtnLegend[0]) : []),
    [dtnLegend],
  );
  const [deleteStyleAndUnlink] = useDeleteStyleAndUnlinkMutation();
  const { playbackMode, currentProjection } = useContext(MainAppContext);
  const {
    play, stop, setPreloadingState, timestampList,
  } = useContext(TimeSeriesContext);
  const {
    updateLayerStyle, updateLayerOpacity, map, preloadLayer,
  } = useContext(MapViewContext);
  const { editProduct, selectedProducts } = useContext(ProductsContext);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [currentStyle, setCurrentStyle] = useState(product.currentStyle.substring(product.currentStyle.indexOf(':') + 1));
  const [currentOpacity, setCurrentOpacity] = useState(product.currentOpacity || 100);
  const [currentStyles, setCurrentStyles] = useState(allTheStyles || []);
  const { message } = App.useApp();
  /*   const elevations = capabilities?.Capability
    .Layer
    .Layer
    .find((layer) => layer.Name === product.name)?
    .Dimension?.find((dimension) => dimension.name === 'elevation')?.values;
  const [currentElevation,
    setCurrentElevation] = useState<number | undefined>(elevations
      ? parseElevations(elevations).elevations[0]
      : undefined); */
  const [updateLayerStyleInBack] = useUpdateLayerStyleMutation();
  const [createLayerStyleInBack] = useCreateLayerStyleMutation();
  const style = useMemo(
    () => currentStyles?.find((style) => style.name === currentStyle),
    [currentStyle, currentStyles],
  );
  const colors = useMemo(() => parseFromGeoStyleToColorSteps(style), [style]);
  const rechargingTimeout = useRef<ReturnType<typeof setInterval> | null>(null);

  const recharge = useCallback((map: Map) => {
    const geolayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (geolayer.getLayers().getArray().some((l) => l.get('id') === product.id)) {
      setPreloadingState((prev) => prev.map((s) => (s ? 'active' : s)));
      if (rechargingTimeout.current) {
        clearTimeout(rechargingTimeout.current);
      }
      rechargingTimeout.current = setTimeout(() => {
        preloadLayer({
          index: product.index,
          product,
          generatedTimestampList: timestampList,
        }).then(() => {
          if (playbackMode) {
            stop();
            play();
          }
        }).catch(
          (error) => { logger.error('Config panel layer update failed', error); },
        );
      }, 2000);
    }
  }, [product, setPreloadingState, preloadLayer, timestampList, playbackMode, stop, play]);

  const updateStyle = async (colorSteps: ColorStep[], newName?: string) => {
    const parser = new SldStyleParser();
    setIsOpened((prev) => !prev);
    if (style || currentStyle === 'raster') {
      const newStyle = parseFromColorStepsToGeoStyle(colorSteps, style || styleTemplate);
      if (newName) {
        newStyle.name = newName;
      }
      const index = currentStyles?.findIndex((s) => s.name === newStyle.name);
      if (index !== -1) {
        setCurrentStyles((prev) => {
          const st = [...prev || []];
          st[index] = newStyle;
          return st;
        });
      } else {
        setCurrentStyles((prev) => [...prev, newStyle]);
      }
      const sldObject = await parser
        .writeStyle(newStyle)
        .then(({ output: sldObject }) => {
          if (sldObject) {
            return sldObject.replace(
              /<UserStyle>/,
              '<UserStyle><Abstract>deletable=true</Abstract>',
            );
          }
          return sldObject;
        });

      if (sldObject) {
        if (!newName && style) {
          updateLayerStyleInBack({
            workspace: product.workspace,
            style: `${product.workspace}:${style.name}`,
            rawStyle: sldObject,
          }).then(() => {
            purgeCache(newStyle.name);
            editProduct({ ...product, currentStyle: newStyle.name } as Product);
            if (map) {
              updateGeoLayer(map, currentProjection);
              if (map.getLayers().getArray().find((l) => l.get('id') === product.id)) {
                setPreloadingState((prev) => prev.map((s) => (s ? 'active' : s)));
                if (rechargingTimeout.current) {
                  clearTimeout(rechargingTimeout.current);
                }
                rechargingTimeout.current = setTimeout(() => {
                  preloadLayer({
                    index: product.index,
                    product,
                    generatedTimestampList: timestampList,
                  })
                    .then(() => {
                      if (playbackMode) {
                        stop();
                        play();
                      }
                    }).catch(
                      (error) => { logger.error('Config panel layer update failed', error); },
                    );
                }, 2000);
              }
            }
          }).catch(
            (error) => { message.error(`Error updating style: ${error}`); },
          );
        } else {
          createLayerStyleInBack({
            layer: product.layer.name,
            rawStyle: sldObject,
            styleName: newStyle.name,
          }).then(() => {
            // setCurrentStyles((prev) => [...prev, newStyle]);
            editProduct({ ...product, currentStyle: newStyle.name } as Product);
            updateLayerStyle(product.index, newName || 'new style');
          }).catch(
            (error) => { message.error(`Error creating style: ${error}`); },
          );
        }
      }
    }
  };

  useEffect(() => {
    if (allTheStyles) {
      setCurrentStyles(allTheStyles);
    }
  }, [allTheStyles]);

  useEffect(() => {
    if (product) {
      setCurrentStyle(product.currentStyle.substring(product.currentStyle.indexOf(':') + 1));
    }
  }, [product]);

  // Auto-apply the first DTN style when styles load and none is selected yet
  useEffect(() => {
    if (!isDTN || !dtnStyles?.length || currentStyle) return;
    const first = dtnStyles[0];
    const filterIds = first.filters?.map((f) => f.id) ?? [];
    updateLayerStyle(product.index, first.id, filterIds);
    product.setCurrentStyle(first.id);
    setCurrentStyle(first.id);
    const updatedProducts = selectedProducts.map((p) => (p.id === product.id
      ? { ...p, currentStyle: first.id }
      : p));
    localStorage.setItem('storedProductsList', JSON.stringify(updatedProducts));
  }, [dtnStyles]); // eslint-disable-line react-hooks/exhaustive-deps

  /*   useEffect(() => {
    if (elevations) {
      setCurrentElevation(parseElevations(elevations).elevations[0]);
    }
  }, [elevations]); */

  return (
    <Collapse
      items={[
        {
          key: '1',
          label: isDTN
            ? (
              <div className={header} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {dtnLegend?.[0]?.label && <span style={{ fontSize: '0.7em', opacity: 0.6, marginBottom: 2 }}>{dtnLegend[0].label}</span>}
                <ColorScale gradient colors={dtnColors.length ? dtnColors : undefined} />
              </div>
            )
            : <div className={header}><ColorScale gradient colors={colors} /></div>,
          children: (
            <>
              {isDTN && (dtnLegend?.length ?? 0) > 1 && dtnLegend?.slice(1).filter((l) => l.colorTableType !== 'category').map((legend) => (
                <div key={legend.label} style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: '0.8em', opacity: 0.7 }}>{legend.label}</span>
                  <div>
                    <ColorScale gradient colors={legendToColorSteps(legend)} />
                  </div>
                </div>
              ))}
              <div>
                <span>Opacity</span>
                <div>
                  <Slider
                    style={{ width: '100%', paddingLeft: '0px', paddingRight: '0px' }}
                    defaultValue={100}
                    value={currentOpacity}
                    onChange={(value) => {
                      updateLayerOpacity(product.index, value);
                      product.setCurrentOpacity(value);
                      setCurrentOpacity(value);
                    }}
                  />
                </div>
              </div>
              <div>
                <span>Style</span>
                <div>
                  {isDTN ? (
                    <Select
                      size="small"
                      className={selector}
                      value={currentStyle || undefined}
                      placeholder="Default"
                      onChange={(value) => {
                        const selectedStyle = dtnStyles?.find((s) => s.id === value);
                        const filterIds = selectedStyle?.filters?.map((f) => f.id) ?? [];
                        updateLayerStyle(product.index, value, filterIds);
                        product.setCurrentStyle(value);
                        setCurrentStyle(value);
                        const updatedProducts = selectedProducts.map((p) => (p.id === product.id
                          ? { ...p, currentStyle: value }
                          : p));
                        localStorage.setItem('storedProductsList', JSON.stringify(updatedProducts));
                      }}
                    >
                      {dtnStyles?.map((s) => (
                        <Option key={s.id} value={s.id} label={s.label}>
                          {s.label}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <>
                      <Select
                        size="small"
                        className={selector}
                        defaultValue={style?.name}
                        value={currentStyle}
                        optionLabelProp="label"
                        onChange={(value) => {
                          updateLayerStyle(product.index, value);
                          product.setCurrentStyle(value);
                          if (map) {
                            recharge(map);
                          }
                          setCurrentStyle(value);
                          const updatedProducts = selectedProducts.map((p) => (p.id === product.id
                            ? { ...p, currentStyle: value }
                            : p));
                          localStorage.setItem('storedProductsList', JSON.stringify(updatedProducts));
                        }}
                      >
                        {currentStyles?.map((style) => {
                          const isInUse = currentStyle === style.name
                            || selectedProducts.some((p) => p.currentStyle === style.name
                            && p.internalName === product.internalName);
                          return (
                            <Option
                              key={style.name}
                              value={style.name}
                              label={style.name}
                            >
                              <Flex justify="space-between" align="center">
                                {style.name}
                                {' '}
                                {style.deletable ? (
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      Modal.confirm({
                                        title: 'Delete Style',
                                        content: `Are you sure you want to delete the style "${style.name}"?`,
                                        okText: 'Delete',
                                        okType: 'danger',
                                        cancelText: 'Cancel',
                                        onOk: () => {
                                          deleteStyleAndUnlink({
                                            styleName: style.name,
                                            layerName: product.internalName,
                                          }).then(() => {
                                            if (style.name === currentStyle) {
                                              setCurrentStyle(currentStyles[0].name);
                                              const updatedProducts = selectedProducts.map((p) => (
                                                p.id === product.id
                                                  ? { ...p, currentStyle: currentStyles[0].name }
                                                  : p));
                                              localStorage.setItem('storedProductsList', JSON.stringify(updatedProducts));
                                              if (map) {
                                                recharge(map);
                                              }
                                            }
                                          }).catch((error) => {
                                            message.error(`Error deleting style: ${error}`);
                                          });
                                        },
                                      });
                                    }}
                                    disabled={isInUse}
                                    icon={<Tooltip trigger={['hover']} title={isInUse ? 'Current style cannot be deleted since it is in use' : 'Delete style'}><div style={{ display: 'inline-block', pointerEvents: 'auto' }}><DeleteOutlined style={{ fontSize: '0.9em', color: isInUse ? '#ccc' : undefined }} /></div></Tooltip>}
                                    type="link"
                                  />
                                ) : undefined}
                              </Flex>
                            </Option>
                          );
                        })}
                      </Select>
                      {!style && (
                      <Tooltip
                        title="This style may not work properly due to a formatting error."
                      >
                        <WarningOutlined style={{ color: '#FFAA01', marginLeft: '10px' }} />
                      </Tooltip>
                      )}
                      <Button
                        type="default"
                        shape="circle"
                        size="small"
                        icon={<SettingOutlined />}
                        onClick={() => setIsOpened((prev) => !prev)}
                        disabled={!style}
                      />
                    </>
                  )}
                </div>
              </div>
              {!isDTN && isOpened && (
              <ColorScaleEditorModal
                open={isOpened}
                onOk={updateStyle}
                onCancel={() => setIsOpened((prev) => !prev)}
                style={currentStyles?.find(({ name }) => name === currentStyle)}
              />
              )}
            </>
          ),
        },
      ]}
      ghost
    />
  );
}

export default ConfigPanels;
